import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';
import { SignatureCanvas, type SignatureCanvasHandle } from '@/components/SignatureCanvas';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SearchModal } from '@/components/modals/SearchModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { CitationOffenderSection } from '@/components/citation/sections/CitationOffenderSection';
import { CitationVehicleSection } from '@/components/citation/sections/CitationVehicleSection';
import { CitationAuditLogTab } from '@/components/citation/sections/CitationAuditLogTab';
import { PageModal } from '@/components/modals/PageModal';
import { CallCardScreenWrapper } from '@/components/screens/CallCardScreen';

// ============================================================================
// External Declarations
// ============================================================================

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';
import { getModuleType, currentUserHasPermission, hasValue, getOfficerLabel } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';
import { uploadBlockBlob } from '@/services/azure-blob-service';
import { b64toBlob } from '@/utils/blob';
import { saveAs } from 'file-saver';

import { getOfficerPluralLabel, getCurrentUserId, getAuthObject } from '@/utils/auth';
import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp, notifyApp } from '@/utils/window-globals';
import { openPageInNewWindow } from '@/utils/dom';

// Constructors and values: access lazily via (window as any).X at usage sites
let currentCitation: any = null;

// ============================================================================
// Interfaces
// ============================================================================

interface CitationFormOptions {
  mode: 'new' | 'edit' | 'view';
  citationId?: number;
  preFilledPersonId?: number;
  preLinkedCallCardId?: number;
  callCardData?: any;
  parentContext?: 'person' | 'callcard' | 'citations';
  parentId?: number;
}

interface ValidationErrors {
  [key: string]: string;
}

interface SignatureUploadResult {
  attachmentId: number;
  activationKey: string;
  signatureUrl: string;
}

interface CitationFormScreenProps {
  options: CitationFormOptions;
}

// ============================================================================
// Helpers
// ============================================================================

function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)![1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

function handleCitationApiError(error: any, defaultMessage: string): boolean {
  if (error && error.status === 409) {
    let conflictMessage = 'This citation was modified by another user. Please reload and try again.';
    if (error.responseJSON) {
      const resp = error.responseJSON;
      if (resp.modifiedBy || resp.modifiedByName) {
        conflictMessage = `This citation was modified by ${resp.modifiedByName || 'another user'} at ${resp.modifiedDate ? moment(resp.modifiedDate).format('MM/DD/YYYY HH:mm:ss') : 'unknown time'}. Please reload and try again.`;
      }
    }
    App.showPopupNotification(conflictMessage, 'warning');
    return true;
  }
  if (error && error.responseJSON) {
    const errorCode = error.responseJSON.errorCode || error.responseJSON.code;
    if (errorCode === 'com.kerkton.orna.citation.callCard.invalid') {
      App.showPopupNotification('The linked call card is invalid or does not exist.', 'error');
      return true;
    }
    if (errorCode === 'com.kerkton.orna.citation.callCard.invalidCompany') {
      App.showPopupNotification('The linked call card belongs to a different company.', 'error');
      return true;
    }
  }
  App.showPopupNotification(defaultMessage, 'error');
  return false;
}

function uploadSignatureAttachment(signatureDataUrl: string, typeName = 'citation_attachment'): Promise<SignatureUploadResult> {
  return new Promise((resolve, reject) => {
    const file = dataURLtoFile(signatureDataUrl, 'signature.png');
    const attachment = new (window as any).Attachment();
    attachment.title = 'Violator Signature';
    attachment.filename = 'signature.png';
    attachment.typeName = typeName;

    api.saveAttachment([attachment], (result: any) => {
      if (!result.azureStorageBlobInfoList || result.azureStorageBlobInfoList.length === 0) {
        reject(new Error('No blob info returned from server'));
        return;
      }
      const blobInfo = result.azureStorageBlobInfoList[0];
      const signatureUrl = `${blobInfo.host}/${blobInfo.containerName}/${blobInfo.blobName}?${blobInfo.sasReadToken}`;
      uploadBlockBlob(blobInfo.host, blobInfo.sasWriteToken, blobInfo.containerName, blobInfo.blobName, file).then(() => {
        resolve({ attachmentId: blobInfo.attachmentId, activationKey: blobInfo.activationKey, signatureUrl });
      }).catch((error: any) => {
        reject(error);
      });
    });
  });
}

function getSignatureDataUrl(signatureMethod: string, signatureData: string | null, typedSignature: string): string | null {
  if (signatureMethod === 'draw' && signatureData) {
    return signatureData;
  } else if (signatureMethod === 'typed' && typedSignature) {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 150;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'black';
    ctx.font = 'italic 32px "Brush Script MT", cursive';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL('image/png');
  }
  return null;
}

function applyCallCardDefaults(citation: any, callCardData: any) {
  if (!callCardData) return;
  citation.propertyLocation = callCardData.callLocFormattedAddress || '';
  citation.offenseLocation = callCardData.callLocFormattedAddress || '';
  citation.cadEventNumber = callCardData.eventId || '';
  if (callCardData.callDateTime) {
    citation.offenseDateTime = new Date(callCardData.callDateTime);
  }
}

function closeCitationForm() {
  history.back();
}

// ============================================================================
// Component
// ============================================================================

export function CitationFormScreen({ options }: CitationFormScreenProps) {
  const { theme } = useTheme();

  // Core state
  const [citation, setCitation] = useState<any>(() => {
    if (options.mode === 'new') {
      const c = new (window as any).Citation(null);
      c.status = 'draft';
      c.dateOfIssuance = new Date();
      c.offenseDateTime = new Date();
      c.issuingOfficerId = getCurrentUserId();
      if (options.preFilledPersonId) c.npsdPersonId = options.preFilledPersonId;
      if (options.preLinkedCallCardId) c.callCardId = options.preLinkedCallCardId;
      if (options.callCardData) applyCallCardDefaults(c, options.callCardData);
      currentCitation = c;
      return c;
    }
    return new (window as any).Citation(null);
  });

  const [mode, setMode] = useState<'new' | 'edit' | 'view'>(options.mode);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [officerLabel] = useState(() => getOfficerLabel());
  const [officersList, setOfficersList] = useState<any[]>([]);
  const [violationTypeOptions, setViolationTypeOptions] = useState<Array<{ code: number; value: string }>>([]);
  const [isCourtDateEnabled, setIsCourtDateEnabled] = useState(false);
  const [isPublicNotesEnabled, setIsPublicNotesEnabled] = useState(false);

  const [offenderMode, setOffenderMode] = useState(0);
  const [includeAttachmentImages, setIncludeAttachmentImages] = useState(false);

  // Call card state
  const [linkedCallCard, setLinkedCallCard] = useState<any>(options.callCardData || null);

  // Signature state
  const [signatureMethod, setSignatureMethod] = useState<'draw' | 'typed'>('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const signatureCanvasRef = useRef<SignatureCanvasHandle>(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Modal state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isVoiding, setIsVoiding] = useState<boolean>(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [unsavedModalAction, setUnsavedModalAction] = useState<'close' | 'cancelEdit'>('close');
  const [showPdfDownloadModal, setShowPdfDownloadModal] = useState(false);
  const [pdfVersion, setPdfVersion] = useState<'public' | 'internal'>('public');
  const [showCallCardSearchModal, setShowCallCardSearchModal] = useState(false);
  const [showRemoveAttachmentModal, setShowRemoveAttachmentModal] = useState(false);
  const [removeAttachmentIndex, setRemoveAttachmentIndex] = useState<number | null>(null);
  const [showDeleteDraftModal, setShowDeleteDraftModal] = useState(false);

  // Call card view modal state
  const [showCallCardViewModal, setShowCallCardViewModal] = useState(false);
  const [callCardViewModalId, setCallCardViewModalId] = useState<number | null>(null);

  // Attachment state
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<'details' | 'auditLog'>('details');
  const [auditLogTotalRecords, setAuditLogTotalRecords] = useState(0);

  // ============================================================================
  // Computed values
  // ============================================================================

  const headerTitle = useMemo(() => {
    if (mode === 'new') return 'New Citation';
    if (mode === 'edit') return 'Edit Citation';
    return 'View Citation';
  }, [mode]);

  const isEditable = mode === 'new' || mode === 'edit';

  const isIssuedFieldLocked = citation.status === 'issued' || citation.status === 'voided';

  const isAllFieldsReadOnly = mode === 'view' || citation.status === 'voided';

  const canEdit = useMemo(() => {
    if (citation.status === 'voided') return false;
    if (currentUserHasPermission(AccessAuthorities.ROLE_CITATION_EDIT_ALL)) return true;
    if (currentUserHasPermission(AccessAuthorities.ROLE_CITATION_EDIT)) {
      const uid = getCurrentUserId();
      return citation.createdBy === uid || citation.issuingOfficerId === uid;
    }
    return false;
  }, [citation.status, citation.createdBy, citation.issuingOfficerId]);

  const canCreate = currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE);
  const canVoid = citation.status === 'issued' && currentUserHasPermission(AccessAuthorities.ROLE_CITATION_VOID);
  const canDeleteDraft = citation.status === 'draft' && currentUserHasPermission(AccessAuthorities.ROLE_CITATION_DELETE);
  const canDownloadPublicPdf = citation.status === 'issued' || citation.status === 'voided';
  const canDownloadInternalPdf = currentUserHasPermission(AccessAuthorities.ROLE_CITATION_INTERNAL_PDF);
  const canViewAuditLog = !!citation.id && currentUserHasPermission(AccessAuthorities.ROLE_CITATION_AUDIT_LOG);

  const isFieldDisabled = useCallback((fieldName: string): boolean => {
    if (mode === 'view' || citation.status === 'voided') return true;
    if (citation.status !== 'issued') return false;
    const editableFieldsWhenIssued = ['notes', 'publicNotes', 'probableCauseNarrative', 'bodyCamReference', 'courtDateTime', 'attachmentList'];
    return editableFieldsWhenIssued.indexOf(fieldName) === -1;
  }, [mode, citation.status]);

  const hasSavedSignature = !!citation.signatureAttachmentId && !!citation.signatureUrl && !citation.signatureRefused;

  const officerSelectItems = useMemo(() =>
    (officersList || []).map((o: any) => ({ value: o.id, label: o.name })),
  [officersList]);

  const trespassStatusOptions = useMemo(() => [
    { value: null, label: 'None' },
    { value: 'verbal', label: 'Verbal Warning' },
    { value: 'police', label: 'Police Notified' },
  ], []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Load citation for edit/view modes
  useEffect(() => {
    if (options.mode !== 'new' && options.citationId) {
      api.getCitationById(options.citationId,
        (result: any) => {
          const c = new (window as any).Citation(result);
          setCitation(c);
          currentCitation = c;
          // Person/vehicle loading is handled by section components reacting to citation changes
          if (c.callCardId) {
            loadLinkedCallCard(c.callCardId);
          }
        },
        () => {
          App.showPopupNotification('Citation not found.', 'error');
        }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load officers
  useEffect(() => {
    api.getAvailableMDTOfficers((result: any) => {
      if (result?.officerList) {
        setOfficersList(result.officerList.map((o: any) => ({
          id: o.id,
          name: `${o.firstName} ${o.lastName}`,
        })));
      }
    });
  }, []);

  // Load violation types
  useEffect(() => {
    api.getViolationTypes(
      (result: any) => {
        if (Array.isArray(result)) {
          setViolationTypeOptions(result.sort((a: any, b: any) => {
            if (a.code === 16) return 1;
            if (b.code === 16) return -1;
            return 0;
          }));
        }
      },
      (error: any) => {
        console.error('Failed to load violation types:', error);
        App.showPopupNotification('Failed to load violation types. Please refresh the page.', 'error');
      }
    );
  }, []);

  // Load citation settings
  useEffect(() => {
    api.getCitationSettings(
      (settings: any) => {
        setIsCourtDateEnabled(settings?.isCourtDateEnabled || false);
        setIsPublicNotesEnabled(settings?.isPublicNotesEnabled || false);
        setOffenderMode(settings?.offenderMode ?? 0);
      },
      () => { /* ignore */ }
    );
  }, []);

  // Load pre-linked call card for new citations
  useEffect(() => {
    if (options.mode === 'new' && options.preLinkedCallCardId && !options.callCardData) {
      loadLinkedCallCard(options.preLinkedCallCardId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================================
  // Data loading helpers
  // ============================================================================

  const loadLinkedCallCard = useCallback((callCardId: number) => {
    api.getCallCardById(callCardId, false, (result: any) => {
      setLinkedCallCard(result);
    });
  }, []);

  // ============================================================================
  // Section callbacks (offender/vehicle)
  // ============================================================================

  const handleCitationPersonIdChange = useCallback((personId: number | null) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.npsdPersonId = personId;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
  }, []);

  const handleCitationVehicleIdChange = useCallback((vehicleId: number | null) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.npsdVehicleId = vehicleId;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
  }, []);

  const handleSectionDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  // ============================================================================
  // Call card handlers
  // ============================================================================

  const handleLinkCallCard = useCallback(() => {
    setShowCallCardSearchModal(true);
  }, []);

  const handleCallCardSearchSelect = useCallback((callCardData: any) => {
    setShowCallCardSearchModal(false);
    if (!callCardData) return;
    const callCardId = callCardData.id || callCardData.callCardId;
    if (!callCardId) return;

    setCitation((prev: any) => {
      const c = { ...prev };
      c.callCardId = callCardId;
      currentCitation = c;
      return c;
    });

    let nocValues = callCardData.natureOfCallValues;
    if (nocValues && Array.isArray(nocValues) && nocValues.length > 0) {
      if (typeof nocValues[0] === 'object' && nocValues[0].value) {
        nocValues = nocValues.map((n: any) => n.value);
      }
    }

    setLinkedCallCard({
      id: callCardId,
      eventId: callCardData.eventId || '',
      callDateTime: callCardData.callDateTime || null,
      callLocFormattedAddress: callCardData.callLocFormattedAddress || '',
      natureOfCallValues: nocValues || [],
    });
    setIsDirty(true);
  }, []);

  const handleCallCardSearchView = useCallback((callCardData: any) => {
    if (callCardData?.id) {
      setCallCardViewModalId(callCardData.id);
      setShowCallCardViewModal(true);
    }
  }, []);

  const handleUnlinkCallCard = useCallback(() => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.callCardId = null;
      currentCitation = c;
      return c;
    });
    setLinkedCallCard(null);
    setIsDirty(true);
  }, []);

  const handleViewLinkedCallCard = useCallback(() => {
    const callCardId = citation.callCardId || linkedCallCard?.id;
    if (callCardId) {
      setCallCardViewModalId(callCardId);
      setShowCallCardViewModal(true);
    }
  }, [citation.callCardId, linkedCallCard]);

  const handleCloseCallCardModal = useCallback(() => {
    setShowCallCardViewModal(false);
    setCallCardViewModalId(null);
  }, []);

  // ============================================================================
  // Citation field handlers
  // ============================================================================

  const updateCitationField = useCallback((field: string, value: any) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c[field] = value;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
    // Clear relevant validation error
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleViolationTypesChange = useCallback((values: any[]) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.violationTypes = values;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.violationTypes;
      delete next.otherViolation;
      delete next.allegedSpeed;
      delete next.speedLimit;
      return next;
    });
  }, []);

  const handleTrespassStatusChange = useCallback((value: string | null) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.trespassStatus = value;
      if (value !== 'police') c.policeCaseNumber = null;
      if (!value) c.trespassDateTime = null;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
  }, []);

  const handleFineToBeDeterminedChange = useCallback((checked: boolean) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.fineToBeDetermined = checked;
      if (checked) c.fineAmount = null;
      currentCitation = c;
      return c;
    });
    setIsDirty(true);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.fineAmount;
      return next;
    });
  }, []);

  const handleSignatureRefusedChange = useCallback((checked: boolean) => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.signatureRefused = checked;
      currentCitation = c;
      return c;
    });
    if (checked) {
      signatureCanvasRef.current?.clear();
      setSignatureData(null);
    }
    setIsDirty(true);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.signature;
      return next;
    });
  }, []);

  const handleSignatureChange = useCallback((dataUrl: string | null) => {
    setSignatureData(dataUrl);
    setIsDirty(true);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.signature;
      return next;
    });
  }, []);

  const handleReplaceSignature = useCallback(() => {
    setCitation((prev: any) => {
      const c = { ...prev };
      c.signatureAttachmentId = null;
      c.signatureAttachmentActivationKey = null;
      c.signatureUrl = null;
      currentCitation = c;
      return c;
    });
    setSignatureData(null);
    setTypedSignature('');
    signatureCanvasRef.current?.clear();
    setSignatureMethod('draw');
    setIsDirty(true);
  }, []);

  // ============================================================================
  // Validation
  // ============================================================================

  const validateCommon = useCallback((): ValidationErrors => {
    const errors: ValidationErrors = {};
    if (offenderMode === 2 && citation.npsdPersonId) errors.offender = 'Offender section is disabled. An offender cannot be attached to this citation.';
    if (!citation.npsdPersonId && offenderMode === 0) errors.offender = 'Offender is required. Please select or create a person record.';
    if (!citation.dateOfIssuance) {
      errors.dateOfIssuance = 'Date of issuance is required.';
    } else if (moment(citation.dateOfIssuance).isAfter(moment())) {
      errors.dateOfIssuance = 'Date of issuance cannot be in the future.';
    }
    if (!citation.issuingOfficerId) errors.issuingOfficerId = 'Issuing officer is required.';
    if (!citation.propertyLocation?.trim()) errors.propertyLocation = 'Property/Location is required.';
    if (!citation.violationTypes || citation.violationTypes.length === 0) {
      errors.violationTypes = 'At least one violation type is required.';
    }
    if (citation.violationTypes?.includes(16) && !citation.otherViolation?.trim()) {
      errors.otherViolation = 'Violation description is required when "Other" is selected.';
    }
    if (!citation.offenseDateTime) {
      errors.offenseDateTime = 'Offense date/time is required.';
    } else if (moment(citation.offenseDateTime).isAfter(moment())) {
      errors.offenseDateTime = 'Offense date/time cannot be in the future.';
    }
    if (!citation.offenseLocation?.trim()) errors.offenseLocation = 'Offense location is required.';

    // Speed validation
    const hasSpeedingViolation = citation.violationTypes?.includes(19);
    const hasParkingViolation = citation.violationTypes?.includes(6);
    if (hasSpeedingViolation) {
      if (!citation.allegedSpeed || citation.allegedSpeed <= 0) errors.allegedSpeed = 'Alleged speed is required and must be positive for speeding violations.';
      if (!citation.speedLimit || citation.speedLimit <= 0) errors.speedLimit = 'Speed limit is required and must be positive for speeding violations.';
    } else if (hasParkingViolation) {
      if (citation.allegedSpeed != null) errors.allegedSpeed = 'Alleged speed is not allowed for parking violations.';
      if (citation.speedLimit != null) errors.speedLimit = 'Speed limit is not allowed for parking violations.';
    } else {
      if (citation.allegedSpeed != null && citation.allegedSpeed <= 0) errors.allegedSpeed = 'Alleged speed must be a positive number.';
      if (citation.speedLimit != null && citation.speedLimit <= 0) errors.speedLimit = 'Speed limit must be a positive number.';
    }

    // Trespass validation
    if (citation.trespassStatus === 'police') {
      if (!citation.policeCaseNumber?.trim()) errors.policeCaseNumber = 'Police case number is required when status is Police Notified.';
      else if (citation.policeCaseNumber.length > 50) errors.policeCaseNumber = 'Police case number must not exceed 50 characters.';
    }
    if ((citation.trespassStatus === 'police' || citation.trespassStatus === 'verbal') && !citation.trespassDateTime) {
      errors.trespassDateTime = 'Trespass date/time is required when a trespass status is selected.';
    } else if (citation.trespassDateTime && moment(citation.trespassDateTime).isAfter(moment())) {
      errors.trespassDateTime = 'Trespass date/time cannot be in the future.';
    }

    // Court date
    if (citation.courtDateTime && citation.offenseDateTime && moment(citation.courtDateTime).isBefore(moment(citation.offenseDateTime))) {
      errors.courtDateTime = 'Court date/time cannot be before offense date/time.';
    }

    // Fine
    if (!citation.fineToBeDetermined && (citation.fineAmount == null)) {
      errors.fineAmount = 'Fine amount is required, or check "Fine to be determined".';
    }

    return errors;
  }, [citation, offenderMode]);

  const validateForSave = useCallback((): boolean => {
    const errors = validateCommon();
    setValidationErrors(errors);
    setShowValidationErrors(true);
    return Object.keys(errors).length === 0;
  }, [validateCommon]);

  const validateForIssuance = useCallback((): boolean => {
    const errors = validateCommon();

    const hasDrawnSig = signatureMethod === 'draw' && signatureData;
    const hasTypedSig = signatureMethod === 'typed' && typedSignature;
    const hasAnySig = hasDrawnSig || hasTypedSig || citation.signatureAttachmentId;

    if (citation.signatureRefused && hasAnySig) {
      errors.signature = 'Cannot have both a signature and "Violator refused to sign" checked.';
    } else if (!citation.signatureRefused && !hasDrawnSig && !hasTypedSig) {
      errors.signature = 'Signature is required, or check "Violator refused to sign".';
    }

    setValidationErrors(errors);
    setShowValidationErrors(true);
    return Object.keys(errors).length === 0;
  }, [validateCommon, signatureMethod, signatureData, typedSignature, citation.signatureRefused, citation.signatureAttachmentId]);

  // ============================================================================
  // Save / Issue / Void / Delete actions
  // ============================================================================

  const saveDraft = useCallback(() => {
    if (!validateForSave()) {
      App.showPopupNotification('Unable to save. Please fix the form errors.', 'error');
      return;
    }
    setIsSaving(true);
    const isNew = !citation.id;

    const doSave = () => {
      const onSuccess = (result: any) => {
        const preservedUrl = citation.signatureUrl;
        const c = new (window as any).Citation(result);
        if (!c.signatureUrl && preservedUrl) c.signatureUrl = preservedUrl;
        setCitation(c);
        currentCitation = c;
        App.showPopupNotification('Draft saved successfully.', 'success');
        setMode('edit');
        setIsSaving(false);
        setIsDirty(false);
        if (isNew && c.id) {
          const moduleType = getModuleType().toUpperCase();
          history.replaceState(null, '', `/?sectionName=${moduleType}&tab=Citations&function=Open_CitationForm&multiModule=true&params=edit,,,${c.id}`);
        }
      };
      const onError = (error: any) => {
        handleCitationApiError(error, 'Failed to save draft.');
        setIsSaving(false);
      };
      if (isNew) {
        api.saveCitation(citation, onSuccess, onError);
      } else {
        api.updateCitation(citation, citation.modifiedDate, onSuccess, onError);
      }
    };

    if (citation.signatureRefused || citation.signatureAttachmentId) {
      doSave();
      return;
    }

    const sigDataUrl = getSignatureDataUrl(signatureMethod, signatureData, typedSignature);
    if (sigDataUrl) {
      uploadSignatureAttachment(sigDataUrl)
        .then((result) => {
          citation.signatureAttachmentId = result.attachmentId;
          citation.signatureAttachmentActivationKey = result.activationKey;
          citation.signatureUrl = result.signatureUrl;
          doSave();
        })
        .catch(() => {
          App.showPopupNotification('Failed to upload signature.', 'error');
          setIsSaving(false);
        });
    } else {
      doSave();
    }
  }, [citation, validateForSave, signatureMethod, signatureData, typedSignature]);

  const issueCitation = useCallback(() => {
    if (!validateForIssuance()) {
      App.showPopupNotification('Unable to issue. Please fix the form errors.', 'error');
      return;
    }
    setIsSaving(true);

    const doIssue = () => {
      const preservedUrl = citation.signatureUrl;
      api.issueCitation(citation.id, citation.modifiedDate,
        (result: any) => {
          const c = new (window as any).Citation(result);
          if (!c.signatureUrl && preservedUrl) c.signatureUrl = preservedUrl;
          setCitation(c);
          currentCitation = c;
          App.showPopupNotification(`Citation issued: ${c.citationNumber}`, 'success');
          setMode('view');
          setIsSaving(false);
          setIsDirty(false);
        },
        (error: any) => {
          handleCitationApiError(error, 'Failed to issue citation.');
          setIsSaving(false);
        }
      );
    };

    const saveAndIssue = () => {
      if (!citation.id) {
        api.saveCitation(citation,
          (result: any) => {
            const c = new (window as any).Citation(result);
            setCitation(c);
            currentCitation = c;
            citation.id = c.id;
            citation.modifiedDate = c.modifiedDate;
            doIssue();
          },
          (error: any) => {
            handleCitationApiError(error, 'Failed to save citation before issuing.');
            setIsSaving(false);
          }
        );
      } else {
        api.updateCitation(citation, citation.modifiedDate,
          (result: any) => {
            const c = new (window as any).Citation(result);
            setCitation(c);
            currentCitation = c;
            citation.id = c.id;
            citation.modifiedDate = c.modifiedDate;
            doIssue();
          },
          (error: any) => {
            handleCitationApiError(error, 'Failed to update citation before issuing.');
            setIsSaving(false);
          }
        );
      }
    };

    if (citation.signatureRefused || citation.signatureAttachmentId) {
      saveAndIssue();
      return;
    }

    const sigDataUrl = getSignatureDataUrl(signatureMethod, signatureData, typedSignature);
    if (sigDataUrl) {
      uploadSignatureAttachment(sigDataUrl)
        .then((result) => {
          citation.signatureAttachmentId = result.attachmentId;
          citation.signatureAttachmentActivationKey = result.activationKey;
          citation.signatureUrl = result.signatureUrl;
          saveAndIssue();
        })
        .catch(() => {
          App.showPopupNotification('Failed to upload signature.', 'error');
          setIsSaving(false);
        });
    } else {
      App.showPopupNotification('No signature data found.', 'error');
      setIsSaving(false);
    }
  }, [citation, validateForIssuance, signatureMethod, signatureData, typedSignature]);

  const saveChanges = useCallback(() => {
    setIsSaving(true);
    api.updateCitation(citation, citation.modifiedDate,
      (result: any) => {
        const c = new (window as any).Citation(result);
        setCitation(c);
        currentCitation = c;
        App.showPopupNotification('Changes saved successfully.', 'success');
        setMode('view');
        setIsSaving(false);
        setIsDirty(false);
      },
      (error: any) => {
        handleCitationApiError(error, 'Failed to save changes.');
        setIsSaving(false);
      }
    );
  }, [citation]);

  const deleteDraft = useCallback(() => {
    setShowDeleteDraftModal(true);
  }, []);

  const handleConfirmDeleteDraft = useCallback(() => {
    setShowDeleteDraftModal(false);
    if (citation.id) {
      api.deleteCitation(citation.id,
        () => {
          App.showPopupNotification('Draft deleted.', 'success');
          closeCitationForm();
        },
        () => App.showPopupNotification('Failed to delete draft.', 'error')
      );
    } else {
      closeCitationForm();
    }
  }, [citation.id]);

  const handleCancelForm = useCallback(() => {
    if (isDirty) {
      setUnsavedModalAction('close');
      setShowUnsavedModal(true);
    } else {
      closeCitationForm();
    }
  }, [isDirty]);

  const handleCancelEditMode = useCallback(() => {
    if (isDirty) {
      setUnsavedModalAction('cancelEdit');
      setShowUnsavedModal(true);
      return;
    }
    setMode('view');
  }, [isDirty]);

  const handleConfirmDiscard = useCallback(() => {
    setShowUnsavedModal(false);
    setIsDirty(false);
    if (unsavedModalAction === 'cancelEdit') {
      setMode('view');
      if (citation.id) {
        api.getCitationById(citation.id,
          (result: any) => {
            const c = new (window as any).Citation(result);
            setCitation(c);
            currentCitation = c;
          },
          () => App.showPopupNotification('Failed to reload citation.', 'error')
        );
      }
    } else {
      closeCitationForm();
    }
  }, [unsavedModalAction, citation.id]);

  const justEnteredEditModeRef = useRef(false);

  const handleEnterEditMode = useCallback(() => {
    justEnteredEditModeRef.current = true;
    setMode('edit');
  }, []);

  // Clear isDirty after entering edit mode (setMode('edit') may trigger effects that set isDirty)
  useEffect(() => {
    if (mode === 'edit' && justEnteredEditModeRef.current) {
      justEnteredEditModeRef.current = false;
      setIsDirty(false);
    }
  }, [mode]);

  const handleVoidCitation = useCallback(() => {
    setVoidReason('');
    setShowVoidModal(true);
  }, []);

  const handleConfirmVoid = useCallback(() => {
    if (!voidReason.trim()) {
      App.showPopupNotification('Please provide a reason for voiding.', 'warning');
      return;
    }
    setIsVoiding(true);
    api.voidCitation(citation.id, voidReason, citation.modifiedDate,
      (result: any) => {
        setIsVoiding(false);
        const c = new (window as any).Citation(result);
        setCitation(c);
        currentCitation = c;
        App.showPopupNotification('Citation voided.', 'success');
        setShowVoidModal(false);
        setMode('view');
      },
      (error: any) => {
        setIsVoiding(false);
        handleCitationApiError(error, 'Failed to void citation.');
      }
    );
  }, [citation.id, citation.modifiedDate, voidReason]);

  // ============================================================================
  // PDF download
  // ============================================================================

  const downloadPdf = useCallback((version: 'public' | 'internal' = 'public') => {
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    api.getCitationPdf(citation.id, timeZoneId, version,
      (result: any) => {
        if (result.pdfBase64 && result.pdfFilename) {
          if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
            notifyApp('activityReportPDFBase64', [result.pdfFilename, result.pdfBase64]);
          } else {
            const blob = b64toBlob(result.pdfBase64, 'application/pdf');
            saveAs(blob, result.pdfFilename);
          }
        } else {
          App.showPopupNotification('PDF generation failed.', 'error');
        }
      },
      () => App.showPopupNotification('Failed to generate PDF.', 'error'),
      includeAttachmentImages
    );
  }, [citation.id, includeAttachmentImages]);

  // ============================================================================
  // Attachments
  // ============================================================================

  const handleAddAttachment = useCallback(() => {
    attachmentFileInputRef.current?.click();
  }, []);

  const handleAttachmentFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    if (!input.files || input.files.length === 0) return;
    const files = Array.from(input.files);
    const totalFiles = files.length;
    setIsUploadingAttachment(true);

    const attachments: any[] = files.map((file) => {
      const a = new (window as any).Attachment();
      a.title = file.name;
      a.filename = file.name;
      a.typeName = 'citation_attachment';
      return a;
    });

    api.saveAttachment(attachments, (result: any) => {
      if (!result.azureStorageBlobInfoList || result.azureStorageBlobInfoList.length === 0) {
        App.showPopupNotification('Failed to prepare attachment upload.', 'error');
        setIsUploadingAttachment(false);
        return;
      }
      const blobInfoList = result.azureStorageBlobInfoList;
      let completed = 0;
      let failed = 0;

      files.forEach((file, index) => {
        const blobInfo = blobInfoList[index];
        if (!blobInfo) {
          failed++;
          checkComplete(completed + failed, totalFiles, failed);
          return;
        }
        const signedURL = `${blobInfo.host}/${blobInfo.containerName}/${blobInfo.blobName}?${blobInfo.sasReadToken}`;
        uploadBlockBlob(blobInfo.host, blobInfo.sasWriteToken, blobInfo.containerName, blobInfo.blobName, file).then(() => {
          const newAttachment = new (window as any).Attachment();
          newAttachment.id = blobInfo.attachmentId;
          newAttachment.title = file.name;
          newAttachment.filename = file.name;
          newAttachment.signedURL = signedURL;
          newAttachment.createdBy = getAuthObject()?.username || '';
          newAttachment.dateCreated = new Date().toISOString();
          (newAttachment as any).activationKey = blobInfo.activationKey;

          setCitation((prev: any) => {
            const c = { ...prev };
            c.attachmentList = [...(c.attachmentList || []), newAttachment];
            currentCitation = c;
            return c;
          });
          setIsDirty(true);
          completed++;
          checkComplete(completed + failed, totalFiles, failed);
        }).catch(() => {
          failed++;
          checkComplete(completed + failed, totalFiles, failed);
        });
      });
    });
    input.value = '';
  }, []);

  const checkComplete = useCallback((processed: number, total: number, failed: number) => {
    if (processed >= total) {
      setIsUploadingAttachment(false);
      const successful = total - failed;
      if (failed === 0) App.showPopupNotification(`${successful} attachment${successful > 1 ? 's' : ''} uploaded successfully.`, 'success');
      else if (successful > 0) App.showPopupNotification(`${successful} uploaded, ${failed} failed.`, 'warning');
      else App.showPopupNotification('Failed to upload attachments.', 'error');
    }
  }, []);

  const handleRemoveAttachment = useCallback((index: number) => {
    setRemoveAttachmentIndex(index);
    setShowRemoveAttachmentModal(true);
  }, []);

  const handleConfirmRemoveAttachment = useCallback(() => {
    if (removeAttachmentIndex != null) {
      setCitation((prev: any) => {
        const c = { ...prev };
        c.attachmentList = [...c.attachmentList];
        c.attachmentList.splice(removeAttachmentIndex, 1);
        currentCitation = c;
        return c;
      });
      setIsDirty(true);
    }
    setShowRemoveAttachmentModal(false);
    setRemoveAttachmentIndex(null);
  }, [removeAttachmentIndex]);

  // ============================================================================
  // Tab switching
  // ============================================================================

  const handleSwitchTab = useCallback((tab: 'details' | 'auditLog') => {
    setActiveTab(tab);
  }, []);

  // ============================================================================
  // Helper render functions
  // ============================================================================

  const formatDateTime = (dateVal: any): string => {
    if (!dateVal) return '';
    return moment(dateVal).format('MM/DD/YYYY HH:mm');
  };

  const isImageAttachment = (attachment: any): boolean => {
    const filename = (attachment.filename || attachment.title || '').toLowerCase();
    return /\.(png|jpg|jpeg|gif|webp)$/.test(filename);
  };

  // ============================================================================
  // Render
  // ============================================================================

  const violationTypeItems = useMemo(() =>
    violationTypeOptions.map((vt) => ({ label: vt.value, value: vt.code })),
  [violationTypeOptions]);

  const showOtherViolationField = useMemo(() =>
    citation.violationTypes?.includes(16),
  [citation.violationTypes]);

  // Speed fields are always visible (not conditionally hidden)

  return (
    <div className={`citation-form ${theme}`}>
      {/* Header */}
      <div className="form-header">
        <div className="form-header-left">
          <h1>{headerTitle}</h1>
          <span className="citation-number">{citation.citationNumber || ''}</span>
          <span className={`status-badge ${citation.status || 'draft'}`}>
            {(citation.status || 'draft').toUpperCase()}
          </span>
        </div>
        <div className="header-actions">
          {/* New Mode Buttons (requires ROLE_CITATION_CREATE) */}
          {mode === 'new' && canCreate && (
            <>
              <button type="button" className="btn btn-secondary" onClick={saveDraft} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              <button type="button" className="btn btn-success" onClick={issueCitation} disabled={isSaving}>
                Issue Citation
              </button>
              <button type="button" className="btn btn-outline" onClick={handleCancelForm}>
                Cancel
              </button>
            </>
          )}
          {/* Draft Edit Mode Buttons */}
          {mode === 'edit' && citation.status === 'draft' && (
            <>
              <button type="button" className="btn btn-secondary" onClick={saveDraft} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              <button type="button" className="btn btn-success" onClick={issueCitation} disabled={isSaving}>
                Issue Citation
              </button>
              {canDeleteDraft && (
                <button type="button" className="btn btn-danger" onClick={deleteDraft}>
                  Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={handleCancelEditMode}>
                Cancel
              </button>
            </>
          )}
          {/* Issued Edit Mode */}
          {mode === 'edit' && citation.status === 'issued' && (
            <>
              <button type="button" className="btn btn-success" onClick={saveChanges} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" className="btn btn-outline" onClick={handleCancelEditMode}>
                Cancel
              </button>
            </>
          )}
          {/* View Mode */}
          {mode === 'view' && (
            <>
              {canEdit && (
                <button type="button" className="btn btn-outline" onClick={handleEnterEditMode}>
                  Edit
                </button>
              )}
              {canVoid && (
                <button type="button" className="btn btn-danger" onClick={handleVoidCitation}>
                  Void
                </button>
              )}
              {(canDownloadPublicPdf || canDownloadInternalPdf) && (
                <button type="button" className="btn btn-success" onClick={() => {
                  setPdfVersion(canDownloadPublicPdf ? 'public' : 'internal');
                  setIncludeAttachmentImages(false);
                  setShowPdfDownloadModal(true);
                }}>
                  Download PDF
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Tab Bar (only for existing citations with audit log permission) */}
      {citation.id && canViewAuditLog && (
        <div className="citation-tabs">
          <button
            type="button"
            className={`citation-tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => handleSwitchTab('details')}
          >
            Citation Details
          </button>
          <button
            type="button"
            className={`citation-tab ${activeTab === 'auditLog' ? 'active' : ''}`}
            onClick={() => handleSwitchTab('auditLog')}
          >
            Audit Log
            {auditLogTotalRecords > 0 && (
              <span className="tab-badge">{auditLogTotalRecords}</span>
            )}
          </button>
        </div>
      )}

      {/* Details Tab */}
      {(activeTab === 'details' || !citation.id || !canViewAuditLog) && (
        <div className="form-content">
          {/* Voided Info Banner */}
          {citation.status === 'voided' && (
            <div className="voided-info-banner">
              <div className="voided-info-content">
                <div className="voided-info-title">This citation has been voided</div>
                <div className="voided-info-row">
                  <span className="voided-info-label">Reason:</span>
                  <span className="voided-info-value">{citation.voidedReason || 'No reason provided'}</span>
                </div>
                {citation.voidedByName && (
                  <div className="voided-info-row">
                    <span className="voided-info-label">Voided by:</span>
                    <span className="voided-info-value">{citation.voidedByName}</span>
                  </div>
                )}
                {citation.voidedDate && (
                  <div className="voided-info-row">
                    <span className="voided-info-label">Date:</span>
                    <span className="voided-info-value">{formatDateTime(citation.voidedDate)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 1: Citation Header */}
          <div className="section">
            <div className="section-header"><span className="section-title">Citation Header</span></div>
            <div className="section-body">
              <div className="form-row">
                <div className="form-group quarter-width">
                  <label>Citation Number</label>
                  <div style={{ fontSize: '1.25rem', fontWeight: 600, padding: '0.5rem 0' }}>
                    {citation.citationNumber || (mode === 'new' ? 'NEW' : 'DRAFT')}
                  </div>
                </div>
                <div className={`form-group quarter-width ${validationErrors.dateOfIssuance ? 'has-error' : ''}`}>
                  <label className="required">Date of Issuance</label>
                  <DateTimePicker
                    value={citation.dateOfIssuance}
                    onChange={(val: Date | null) => updateCitationField('dateOfIssuance', val)}
                    type="datetime"
                    clearable={true}
                    appendToBody={true}
                    disabled={isFieldDisabled('dateOfIssuance')}
                  />
                  {validationErrors.dateOfIssuance && (
                    <span className="error-message">{validationErrors.dateOfIssuance}</span>
                  )}
                </div>
                <div className={`form-group quarter-width ${validationErrors.issuingOfficerId ? 'has-error' : ''}`}>
                  <label className="required">Issuing {officerLabel}</label>
                  <Dropdownlist
                    value={citation.issuingOfficerId}
                    items={officerSelectItems}
                    labelKey="label"
                    valueKey="value"
                    placeholder={`Select ${officerLabel}...`}
                    searchable={officerSelectItems.length > 5}
                    disabled={isFieldDisabled('issuingOfficerId')}
                    onChange={(val) => updateCitationField('issuingOfficerId', val)}
                  />
                  {validationErrors.issuingOfficerId && (
                    <span className="error-message">{validationErrors.issuingOfficerId}</span>
                  )}
                </div>
                <div className={`form-group quarter-width ${validationErrors.propertyLocation ? 'has-error' : ''}`}>
                  <label className="required">Property/Location</label>
                  {isFieldDisabled('propertyLocation') && !citation.propertyLocation ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={citation.propertyLocation || ''}
                      onChange={(e) => updateCitationField('propertyLocation', e.target.value)}
                      disabled={isFieldDisabled('propertyLocation')}
                      placeholder="Enter property or location"
                    />
                  )}
                  {validationErrors.propertyLocation && (
                    <span className="error-message">{validationErrors.propertyLocation}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Linked Call */}
          {(citation.callCardId || linkedCallCard || (isEditable && !isIssuedFieldLocked)) && (
            <div className="section">
              <div className="section-header"><span className="section-title">Linked Call</span></div>
              <div className="section-body">
                {/* No call linked state */}
                {!citation.callCardId && !linkedCallCard && (
                  <div className="linked-call-empty">
                    <div className="empty-state-text">
                      <i className="fa fa-phone-square"></i>
                      No call card linked to this citation.
                    </div>
                    {isEditable && !isIssuedFieldLocked && (
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleLinkCallCard}>
                        <i className="fa fa-link"></i> Link Call Card
                      </button>
                    )}
                  </div>
                )}

                {/* Call linked state */}
                {(citation.callCardId || linkedCallCard) && (
                  <div className="linked-call-panel">
                    <div className="linked-call-info">
                      <div className="linked-call-row">
                        <span className="linked-call-label">Event ID</span>
                        <span className="linked-call-value">
                          {linkedCallCard && linkedCallCard.eventId ? (
                            <a href="#" className="call-link" onClick={(e) => { e.preventDefault(); handleViewLinkedCallCard(); }}>
                              {linkedCallCard.eventId}
                            </a>
                          ) : (
                            <span className="call-id-only">
                              Call #{citation.callCardId}
                              <a href="#" className="call-link" onClick={(e) => { e.preventDefault(); handleViewLinkedCallCard(); }}> (View)</a>
                            </span>
                          )}
                        </span>
                      </div>
                      {linkedCallCard && linkedCallCard.callDateTime && (
                        <div className="linked-call-row">
                          <span className="linked-call-label">Call Date/Time</span>
                          <span className="linked-call-value">{formatDateTime(linkedCallCard.callDateTime)}</span>
                        </div>
                      )}
                      {linkedCallCard && linkedCallCard.callLocFormattedAddress && (
                        <div className="linked-call-row">
                          <span className="linked-call-label">Location</span>
                          <span className="linked-call-value">{linkedCallCard.callLocFormattedAddress}</span>
                        </div>
                      )}
                      {linkedCallCard && linkedCallCard.natureOfCallValues && linkedCallCard.natureOfCallValues.length > 0 && (
                        <div className="linked-call-row">
                          <span className="linked-call-label">Nature of Call</span>
                          <span className="linked-call-value">{linkedCallCard.natureOfCallValues.join(', ')}</span>
                        </div>
                      )}
                    </div>
                    {isEditable && !isIssuedFieldLocked && (
                      <div className="linked-call-actions">
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={handleUnlinkCallCard}>
                          <i className="fa fa-unlink"></i> Unlink Call
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section 2: Offender (hidden when offenderMode === 2 / Disabled) */}
          {offenderMode !== 2 && (
            <CitationOffenderSection
              citation={citation}
              isEditable={isEditable}
              isIssuedFieldLocked={isIssuedFieldLocked}
              showValidationErrors={showValidationErrors}
              offenderError={validationErrors.offender}
              theme={theme}
              onPersonIdChange={handleCitationPersonIdChange}
              onDirty={handleSectionDirty}
            />
          )}

          {/* Section 3: Vehicle (Optional) */}
          <CitationVehicleSection
            citation={citation}
            isEditable={isEditable}
            isIssuedFieldLocked={isIssuedFieldLocked}
            theme={theme}
            onVehicleIdChange={handleCitationVehicleIdChange}
            onDirty={handleSectionDirty}
          />

          {/* Section 4: Violation */}
          <div className="section">
            <div className="section-header"><span className="section-title">Violation</span></div>
            <div className="section-body">
              <div className={`form-row ${validationErrors.violationTypes ? 'has-error' : ''}`}>
                <div className="form-group full-width">
                  <label className="required">Violation Types</label>
                  <MultiSelect
                    value={citation.violationTypes || []}
                    items={violationTypeItems}
                    labelKey="label"
                    valueKey="value"
                    placeholder="Select violation types..."
                    disabled={isFieldDisabled('violationTypes')}
                    maxDisplay={5}
                    sortItems={false}
                    onChange={handleViolationTypesChange}
                  />
                  {validationErrors.violationTypes && (
                    <span className="error-message">{validationErrors.violationTypes}</span>
                  )}
                </div>
              </div>
              {showOtherViolationField && (
                <div className="form-row">
                  <div className={`form-group full-width ${validationErrors.otherViolation ? 'has-error' : ''}`}>
                    <label className="required">Other Violation Description</label>
                    {isFieldDisabled('otherViolation') && !citation.otherViolation ? (
                      <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                    ) : (
                      <input
                        type="text"
                        className="form-input"
                        value={citation.otherViolation || ''}
                        onChange={(e) => updateCitationField('otherViolation', e.target.value)}
                        disabled={isFieldDisabled('otherViolation')}
                        placeholder="Describe the violation"
                        maxLength={500}
                      />
                    )}
                    {validationErrors.otherViolation && (
                      <span className="error-message">{validationErrors.otherViolation}</span>
                    )}
                    <span className="field-hint">
                      {(citation.otherViolation || '').length} / 500 characters
                    </span>
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className={`form-group half-width ${validationErrors.offenseDateTime ? 'has-error' : ''}`}>
                  <label className="required">Offense Date/Time</label>
                  <DateTimePicker
                    value={citation.offenseDateTime}
                    onChange={(val: Date | null) => updateCitationField('offenseDateTime', val)}
                    type="datetime"
                    clearable={true}
                    appendToBody={true}
                    disabled={isFieldDisabled('offenseDateTime')}
                  />
                  {validationErrors.offenseDateTime && (
                    <span className="error-message">{validationErrors.offenseDateTime}</span>
                  )}
                </div>
                <div className={`form-group half-width ${validationErrors.offenseLocation ? 'has-error' : ''}`}>
                  <label className="required">Offense Location</label>
                  {isFieldDisabled('offenseLocation') && !citation.offenseLocation ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={citation.offenseLocation || ''}
                      onChange={(e) => updateCitationField('offenseLocation', e.target.value)}
                      disabled={isFieldDisabled('offenseLocation')}
                      placeholder="Enter offense location"
                    />
                  )}
                  {validationErrors.offenseLocation && (
                    <span className="error-message">{validationErrors.offenseLocation}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className={`form-group quarter-width ${validationErrors.allegedSpeed ? 'has-error' : ''}`}>
                  <label>Alleged Speed (MPH)</label>
                  {isFieldDisabled('allegedSpeed') && citation.allegedSpeed == null ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <input
                      type="number"
                      className="form-input"
                      value={citation.allegedSpeed ?? ''}
                      onChange={(e) => updateCitationField('allegedSpeed', e.target.value ? Number(e.target.value) : null)}
                      disabled={isFieldDisabled('allegedSpeed')}
                      placeholder="Enter speed"
                      min={1}
                      max={999}
                      step={1}
                    />
                  )}
                  {validationErrors.allegedSpeed && (
                    <span className="error-message">{validationErrors.allegedSpeed}</span>
                  )}
                </div>
                <div className={`form-group quarter-width ${validationErrors.speedLimit ? 'has-error' : ''}`}>
                  <label>Posted Speed Limit (MPH)</label>
                  {isFieldDisabled('speedLimit') && citation.speedLimit == null ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <input
                      type="number"
                      className="form-input"
                      value={citation.speedLimit ?? ''}
                      onChange={(e) => updateCitationField('speedLimit', e.target.value ? Number(e.target.value) : null)}
                      disabled={isFieldDisabled('speedLimit')}
                      placeholder="Enter limit"
                      min={1}
                      max={999}
                      step={1}
                    />
                  )}
                  {validationErrors.speedLimit && (
                    <span className="error-message">{validationErrors.speedLimit}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: Fine */}
          <div className="section">
            <div className="section-header"><span className="section-title">Fine</span></div>
            <div className="section-body">
              <div className="form-row">
                <div className={`form-group quarter-width ${validationErrors.fineAmount ? 'has-error' : ''}`}>
                  <label>Fine Amount</label>
                  {(isFieldDisabled('fineAmount') || citation.fineToBeDetermined) && citation.fineAmount == null ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>$</span>
                      <input
                        type="number"
                        className="form-input"
                        value={citation.fineAmount ?? ''}
                        onChange={(e) => updateCitationField('fineAmount', e.target.value ? Number(e.target.value) : null)}
                        disabled={isFieldDisabled('fineAmount') || citation.fineToBeDetermined}
                        placeholder="0.00"
                        step="0.01"
                        min={0}
                      />
                    </div>
                  )}
                  {validationErrors.fineAmount && (
                    <span className="error-message">{validationErrors.fineAmount}</span>
                  )}
                </div>
                <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={citation.fineToBeDetermined || false}
                      onChange={(e) => handleFineToBeDeterminedChange(e.target.checked)}
                      disabled={isFieldDisabled('fineToBeDetermined')}
                    />
                    Fine to be determined by management
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Trespass Information */}
          <div className="section">
            <div className="section-header"><span className="section-title">Trespass Information</span></div>
            <div className="section-body">
              <div className="form-row">
                <div className={`form-group quarter-width ${validationErrors.trespassStatus ? 'has-error' : ''}`}>
                  <label>Trespass Status</label>
                  <Dropdownlist
                    value={citation.trespassStatus}
                    items={trespassStatusOptions}
                    labelKey="label"
                    valueKey="value"
                    placeholder="Select status..."
                    searchable={false}
                    disabled={isFieldDisabled('trespassStatus')}
                    onChange={(val) => handleTrespassStatusChange(val as string | null)}
                  />
                  {validationErrors.trespassStatus && (
                    <span className="error-message">{validationErrors.trespassStatus}</span>
                  )}
                </div>
                {citation.trespassStatus === 'police' && (
                  <div className={`form-group quarter-width ${validationErrors.policeCaseNumber ? 'has-error' : ''}`}>
                    <label className="required">Police Case Number</label>
                    {isFieldDisabled('policeCaseNumber') && !citation.policeCaseNumber ? (
                      <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                    ) : (
                      <input
                        type="text"
                        className="form-input"
                        value={citation.policeCaseNumber || ''}
                        onChange={(e) => updateCitationField('policeCaseNumber', e.target.value)}
                        disabled={isFieldDisabled('policeCaseNumber')}
                        placeholder="Enter case number"
                        maxLength={50}
                      />
                    )}
                    {validationErrors.policeCaseNumber && (
                      <span className="error-message">{validationErrors.policeCaseNumber}</span>
                    )}
                  </div>
                )}
                {(citation.trespassStatus === 'police' || citation.trespassStatus === 'verbal') && (
                  <div className={`form-group quarter-width ${validationErrors.trespassDateTime ? 'has-error' : ''}`}>
                    <label className="required">Trespass Date/Time</label>
                    <DateTimePicker
                      value={citation.trespassDateTime}
                      onChange={(val: Date | null) => updateCitationField('trespassDateTime', val)}
                      type="datetime"
                      clearable={true}
                      appendToBody={true}
                      disabled={isFieldDisabled('trespassDateTime')}
                    />
                    {validationErrors.trespassDateTime && (
                      <span className="error-message">{validationErrors.trespassDateTime}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 6: Probable Cause (Internal Only) */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Probable Cause</span>
              <span className="section-badge">Internal Only</span>
            </div>
            <div className="section-body">
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Probable Cause Narrative</label>
                  {isFieldDisabled('probableCauseNarrative') && !citation.probableCauseNarrative ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>No probable cause narrative</span>
                  ) : (
                    <>
                      <textarea
                        className="form-textarea"
                        value={citation.probableCauseNarrative || ''}
                        onChange={(e) => updateCitationField('probableCauseNarrative', e.target.value)}
                        disabled={isFieldDisabled('probableCauseNarrative')}
                        placeholder="Enter probable cause narrative..."
                        rows={4}
                        maxLength={10000}
                      />
                      <span className="field-hint">
                        {(citation.probableCauseNarrative || '').length} / 10,000 characters
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group half-width">
                  <label>Body Camera Reference</label>
                  {isFieldDisabled('bodyCamReference') && !citation.bodyCamReference ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>None</span>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={citation.bodyCamReference || ''}
                      onChange={(e) => updateCitationField('bodyCamReference', e.target.value)}
                      disabled={isFieldDisabled('bodyCamReference')}
                      placeholder="Enter body camera reference..."
                    />
                  )}
                </div>
                {citation.cadEventNumber && (
                  <div className="form-group half-width">
                    <label>CAD Event Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={citation.cadEventNumber || ''}
                      disabled
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 7: Acknowledgment */}
          <div className="section">
            <div className="section-header"><span className="section-title">Acknowledgment</span></div>
            <div className="section-body">
              <p className="section-description">
                The violator's signature acknowledges receipt of this citation. It does not constitute an admission of guilt. A signature or refusal is required before the citation can be issued.
              </p>

              {validationErrors.signature && (
                <div className="alert alert-danger">{validationErrors.signature}</div>
              )}

              {/* Display saved signature */}
              {hasSavedSignature && (
                <div style={{ marginTop: '1rem' }}>
                  <label>Violator Signature</label>
                  <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '0.5rem', background: '#fff', display: 'inline-block' }}>
                    <img src={citation.signatureUrl} alt="Violator Signature" style={{ maxWidth: '400px', maxHeight: '150px' }} />
                  </div>
                  {isEditable && !isIssuedFieldLocked && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <button type="button" className="btn btn-outline" onClick={handleReplaceSignature}>
                        Replace Signature
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Signature method radio buttons */}
              {isEditable && !isIssuedFieldLocked && !hasSavedSignature && (
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={signatureMethod === 'draw'}
                      onChange={() => setSignatureMethod('draw')}
                      disabled={citation.signatureRefused}
                    />
                    Sign below (touch or mouse)
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      checked={signatureMethod === 'typed'}
                      onChange={() => setSignatureMethod('typed')}
                      disabled={citation.signatureRefused}
                    />
                    Type name as signature
                  </label>
                </div>
              )}

              {/* Signature Canvas */}
              {signatureMethod === 'draw' && !citation.signatureRefused && !isIssuedFieldLocked && !hasSavedSignature && (
                <div style={{ marginTop: '1rem' }}>
                  <SignatureCanvas
                    ref={signatureCanvasRef}
                    height={150}
                    disabled={!isEditable || isIssuedFieldLocked}
                    onChange={handleSignatureChange}
                  />
                  {isEditable && !isIssuedFieldLocked && (
                    <button type="button" className="btn btn-outline" onClick={() => {
                      signatureCanvasRef.current?.clear();
                      setSignatureData(null);
                    }} style={{ marginTop: '0.5rem' }}>
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Typed Signature */}
              {signatureMethod === 'typed' && !citation.signatureRefused && !isIssuedFieldLocked && !hasSavedSignature && (
                <div className="form-row" style={{ marginTop: '1rem' }}>
                  <div className="form-group half-width">
                    <input
                      type="text"
                      className="form-input"
                      value={typedSignature}
                      onChange={(e) => {
                        setTypedSignature(e.target.value);
                        setIsDirty(true);
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next.signature;
                          return next;
                        });
                      }}
                      disabled={!isEditable || isIssuedFieldLocked}
                      placeholder="Type full name"
                    />
                    <span className="field-hint">
                      By typing my name, I acknowledge receipt of this citation.
                    </span>
                  </div>
                </div>
              )}

              {/* Signature Refused */}
              <div style={{ marginTop: '1rem' }}>
                {isEditable && !isIssuedFieldLocked && (
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={citation.signatureRefused || false}
                      onChange={(e) => handleSignatureRefusedChange(e.target.checked)}
                    />
                    Violator refused to sign
                  </label>
                )}
                {citation.signatureRefused && (
                  <div className="alert alert-warning" style={{ marginTop: '0.5rem' }}>
                    Signature refused. Citation remains valid.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 8a: Public Notes (Feature-flagged) */}
          {isPublicNotesEnabled && (
            <div className="section">
              <div className="section-header"><span className="section-title">Public Notes</span></div>
              <div className="section-body">
                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Public Notes (shown on PDF)</label>
                    {isFieldDisabled('publicNotes') && !citation.publicNotes ? (
                      <span className="text-muted" style={{ fontStyle: 'italic' }}>No public notes</span>
                    ) : (
                      <textarea
                        className="form-textarea"
                        value={citation.publicNotes || ''}
                        onChange={(e) => updateCitationField('publicNotes', e.target.value)}
                        disabled={isFieldDisabled('publicNotes')}
                        placeholder="Enter public notes (these will appear on the citation PDF)..."
                        rows={3}
                        maxLength={10000}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 8b: Internal Notes */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Notes</span>
              <span className="section-badge">Internal Only</span>
            </div>
            <div className="section-body">
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Notes</label>
                  {isFieldDisabled('notes') && !citation.notes ? (
                    <span className="text-muted" style={{ fontStyle: 'italic' }}>No notes</span>
                  ) : (
                    <textarea
                      className="form-textarea"
                      value={citation.notes || ''}
                      onChange={(e) => updateCitationField('notes', e.target.value)}
                      disabled={isFieldDisabled('notes')}
                      placeholder="Enter any additional notes..."
                      rows={3}
                      maxLength={10000}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section 9: Court Date (Feature-flagged) */}
          {isCourtDateEnabled && (
            <div className="section">
              <div className="section-header"><span className="section-title">Court Information</span></div>
              <div className="section-body">
                <div className="form-row">
                  <div className={`form-group half-width ${validationErrors.courtDateTime ? 'has-error' : ''}`}>
                    <label>Court Date/Time</label>
                    <DateTimePicker
                      value={citation.courtDateTime}
                      onChange={(val: Date | null) => updateCitationField('courtDateTime', val)}
                      type="datetime"
                      clearable={true}
                      appendToBody={true}
                      disabled={isFieldDisabled('courtDateTime')}
                    />
                    {validationErrors.courtDateTime && (
                      <span className="error-message">{validationErrors.courtDateTime}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Section 10: Attachments (Internal Only) */}
          <div className="section">
            <div className="section-header">
              <span className="section-title">Attachments</span>
              <span className="section-badge">Internal Only</span>
            </div>
            <div className="section-body">
              <div className="attachments-toolbar">
                <button type="button" className="btn btn-outline" onClick={handleAddAttachment} disabled={isFieldDisabled('attachmentList') || isUploadingAttachment}>
                  <i className="fa fa-plus"></i> Add Attachment
                </button>
                {isUploadingAttachment && <span className="upload-status">Uploading...</span>}
              </div>
              <input
                type="file"
                ref={attachmentFileInputRef}
                style={{ display: 'none' }}
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleAttachmentFileSelect}
              />

              {citation.attachmentList && citation.attachmentList.length > 0 ? (
                <div className="attachments-grid">
                  {citation.attachmentList.map((att: any, index: number) => (
                    <div key={att.id || index} className="attachment-card">
                      {!isFieldDisabled('attachmentList') && (
                        <button
                          type="button"
                          className="attachment-remove-btn"
                          onClick={() => handleRemoveAttachment(index)}
                          title="Remove attachment"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      )}
                      <div className="attachment-preview">
                        {isImageAttachment(att) ? (
                          <img src={att.signedURL} alt="Attachment preview" className="attachment-image" />
                        ) : (
                          <div className="attachment-file-icon">
                            <i className="fa fa-file"></i>
                          </div>
                        )}
                      </div>
                      <div className="attachment-info">
                        <a href={att.signedURL} target="_blank" rel="noopener noreferrer" className="attachment-title">{att.title || att.filename || 'Attachment'}</a>
                        {att.createdBy && (
                          <span className="attachment-meta">
                            {att.createdBy} &middot; {formatDateTime(att.dateCreated)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-attachments">
                  No attachments added.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'auditLog' && citation.id && canViewAuditLog && (
        <div className="audit-log-tab-content">
          {/* Voided Info Banner */}
          {citation.status === 'voided' && (
            <div className="voided-info-banner">
              <div className="voided-info-content">
                <div className="voided-info-title">This citation has been voided</div>
                <div className="voided-info-row">
                  <span className="voided-info-label">Reason:</span>
                  <span className="voided-info-value">{citation.voidedReason || 'No reason provided'}</span>
                </div>
                {citation.voidedByName && (
                  <div className="voided-info-row">
                    <span className="voided-info-label">Voided by:</span>
                    <span className="voided-info-value">{citation.voidedByName}</span>
                  </div>
                )}
                {citation.voidedDate && (
                  <div className="voided-info-row">
                    <span className="voided-info-label">Date:</span>
                    <span className="voided-info-value">{formatDateTime(citation.voidedDate)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <CitationAuditLogTab citationId={citation.id} theme={theme} onTotalRecordsChange={setAuditLogTotalRecords} />
        </div>
      )}

      {/* Modals */}
      <ConfirmModal
        open={showVoidModal}
        title="Void Citation"
        confirmText="Void Citation"
        cancelText="Cancel"
        confirmClass="btn-danger"
        confirmDisabled={isVoiding}
        busy={isVoiding}
        theme={theme}
        onConfirm={handleConfirmVoid}
        onCancel={() => setShowVoidModal(false)}
      >
        <p>Are you sure you want to void citation <strong>{citation.citationNumber}</strong>?</p>
        <p>This action cannot be undone.</p>
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label className="required">Reason for voiding:</label>
          <textarea
            className="form-textarea"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Enter reason (required)"
            rows={3}
            maxLength={1000}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={showUnsavedModal}
        title="Unsaved Changes"
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        confirmClass="btn-danger"
        theme={theme}
        onConfirm={handleConfirmDiscard}
        onCancel={() => setShowUnsavedModal(false)}
      >
        <p>You have unsaved changes. Are you sure you want to leave?</p>
        <p>Your changes will be lost.</p>
      </ConfirmModal>

      <ConfirmModal
        open={showPdfDownloadModal}
        title="Download Citation PDF"
        confirmText="Download"
        confirmClass="primary"
        cancelText="Cancel"
        theme={theme}
        onConfirm={() => { setShowPdfDownloadModal(false); downloadPdf(pdfVersion); }}
        onCancel={() => setShowPdfDownloadModal(false)}
      >
        {canDownloadPublicPdf && canDownloadInternalPdf ? (
          <div className="form-group">
            <label className="required">PDF Version</label>
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.25rem' }}>
              <label className="kstc-checkbox-label" style={{ cursor: 'pointer' }}>
                <input type="radio" name="pdfVersion" value="public" checked={pdfVersion === 'public'} onChange={() => setPdfVersion('public')} />
                {' '}Public
              </label>
              <label className="kstc-checkbox-label" style={{ cursor: 'pointer' }}>
                <input type="radio" name="pdfVersion" value="internal" checked={pdfVersion === 'internal'} onChange={() => setPdfVersion('internal')} />
                {' '}Internal
              </label>
            </div>
          </div>
        ) : (
          <p>Generate a {canDownloadInternalPdf ? 'internal' : 'public'} PDF for this citation.</p>
        )}
        {pdfVersion === 'internal' && (
          <div className="kstc-message" style={{ marginTop: '0.75rem', padding: '0.75rem', border: '2px solid #e74c3c', borderRadius: '4px' }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#e74c3c' }}>Do not share this document with the violator.</p>
            <p style={{ margin: '0.4rem 0 0', color: 'inherit' }}>This copy contains sensitive internal information including probable cause narrative, body camera reference, and CAD event number.</p>
          </div>
        )}
        {citation.attachmentList?.length > 0 && (
          <div className="kst-checkbox-group" style={{ marginTop: '1.25rem' }}>
            <input
              type="checkbox"
              id="chkIncludeAttachmentImages"
              className="kstc-checkbox"
              checked={includeAttachmentImages}
              onChange={(e) => setIncludeAttachmentImages(e.target.checked)}
            />
            <label className="kstc-checkbox-label" htmlFor="chkIncludeAttachmentImages">
              Include photo attachments
            </label>
          </div>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={showRemoveAttachmentModal}
        title="Remove Attachment"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="btn-danger"
        theme={theme}
        onConfirm={handleConfirmRemoveAttachment}
        onCancel={() => { setShowRemoveAttachmentModal(false); setRemoveAttachmentIndex(null); }}
      >
        <p>Are you sure you want to remove this attachment?</p>
      </ConfirmModal>

      <ConfirmModal
        open={showDeleteDraftModal}
        title="Delete Draft"
        confirmText="Delete"
        cancelText="Cancel"
        confirmClass="btn-danger"
        theme={theme}
        onConfirm={handleConfirmDeleteDraft}
        onCancel={() => setShowDeleteDraftModal(false)}
      >
        <p>Are you sure you want to delete this draft citation?</p>
      </ConfirmModal>

      {/* Call Card Search Modal */}
      <SearchModal
        open={showCallCardSearchModal}
        searchType={'callcard' as 'callcard'}
        title="Link to Call Card"
        theme={theme}
        onSelect={handleCallCardSearchSelect}
        onView={handleCallCardSearchView}
        onCancel={() => setShowCallCardSearchModal(false)}
      />

      {/* Call Card View Modal */}
      <PageModal
        open={showCallCardViewModal}
        modalId="citationLinkedCallCard"
        title="Call Card"
        theme={theme}
        onClose={handleCloseCallCardModal}
      >
        {showCallCardViewModal && callCardViewModalId && (
          <CallCardScreenWrapper
            callCardId={callCardViewModalId}
            moduleType={getModuleType()}
          />
        )}
      </PageModal>
    </div>
  );
}

(window as any).CitationFormScreenReact = CitationFormScreen;
